import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import api from '../services/api';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Building2,
  Users,
  Briefcase,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';

const OrganizationalSettings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('companies');
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);


  
  // Estados para modais
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Estados para formulários
  const [companyForm, setCompanyForm] = useState({
    name: '',
    cnpj: '',
    address: '',
    phone: '',
    email: ''
  });
  
  const [departmentForm, setDepartmentForm] = useState({
    name: '',
    description: '',
    companyId: ''
  });
  
  const [positionForm, setPositionForm] = useState({
    name: '',
    description: '',
    companyId: '',
    departmentId: ''
  });
  
  const [errors, setErrors] = useState({});
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Primeiro carregar empresas (uma única vez)
      const companiesResponse = await api.get('/organizational/companies');
      
      // Verificar se a resposta tem success ou se tem companies diretamente
      if (companiesResponse.data.success || companiesResponse.data.companies) {
        const companiesData = companiesResponse.data;
        const companiesList = companiesData.companies || [];
        
        setCompanies(companiesList);
        
        // Agora carregar departamentos e cargos usando os dados das empresas
        await Promise.all([
          loadDepartmentsFromCompanies(companiesList),
          loadPositionsFromCompanies(companiesList)
        ]);
      } else {
        console.log('❌ Erro ao carregar empresas:', companiesResponse.data.error);
        toast.error('Erro ao carregar empresas');
      }
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Verificar se o usuário é administrador
  useEffect(() => {
    console.log('🔍 Usuário atual:', user);
    console.log('🔑 Token no localStorage:', localStorage.getItem('token') ? 'Presente' : 'Ausente');
    console.log('🔑 Headers da API:', api.defaults.headers);
    
    if (user && user.role !== 'admin') {
      toast.error('Acesso negado. Apenas administradores podem acessar esta página.');
      return;
    }
  }, [user]);

  // Carregar dados
  useEffect(() => {
    if (user?.role === 'admin') {
      loadData();
    }
  }, [user]);

  const loadDepartmentsFromCompanies = async (companies) => {
    try {
      console.log('📡 Carregando departamentos das empresas...');
      const allDepartments = [];
      
      for (const company of companies) {
        const deptResponse = await api.get(`/organizational/companies/${company.id}/departments`);
        
        console.log(`📡 Resposta departamentos para empresa ${company.name}:`, deptResponse.data);
        
        // Verificar se a resposta tem success ou se tem departments diretamente
        if (deptResponse.data.success || deptResponse.data.departments) {
          const deptData = deptResponse.data;
          const departmentsList = deptData.departments || [];
          console.log(`🏢 Departamentos encontrados para ${company.name}:`, departmentsList);
          allDepartments.push(...departmentsList.map(dept => ({
            ...dept,
            company_id: company.id,
            companyName: company.name
          })));
        } else {
          console.log(`⚠️  Nenhum departamento encontrado para ${company.name}`);
        }
      }
      
      console.log('🏢 Departamentos carregados:', allDepartments);
      setDepartments(allDepartments);
    } catch (error) {
      console.error('❌ Erro ao carregar departamentos:', error);
    }
  };

  const loadPositionsFromCompanies = async (companies) => {
    try {
      console.log('📡 Carregando cargos das empresas...');
      const allPositions = [];
      
      for (const company of companies) {
        const posResponse = await api.get(`/organizational/companies/${company.id}/positions`);
        
        console.log(`📡 Resposta cargos para empresa ${company.name}:`, posResponse.data);
        
        // Verificar se a resposta tem success ou se tem positions diretamente
        if (posResponse.data.success || posResponse.data.positions) {
          const posData = posResponse.data;
          const positionsList = posData.positions || [];
          console.log(`💼 Cargos encontrados para ${company.name}:`, positionsList);
          allPositions.push(...positionsList.map(pos => ({
            ...pos,
            company_id: company.id,
            companyName: company.name
          })));
        } else {
          console.log(`⚠️  Nenhum cargo encontrado para ${company.name}`);
        }
      }
      
      console.log('💼 Cargos carregados:', allPositions);
      setPositions(allPositions);
    } catch (error) {
      console.error('❌ Erro ao carregar cargos:', error);
    }
  };



  // Funções para empresas
  const handleCreateCompany = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!companyForm.name.trim()) newErrors.name = 'Nome da empresa é obrigatório';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const response = await api.post('/organizational/companies', companyForm);

      // Verificar se a resposta tem success ou se tem message (sucesso)
      if (response.data.success || response.data.message) {
        toast.success('Empresa criada com sucesso!');
        setShowCompanyModal(false);
        resetCompanyForm();
        loadData();
      } else {
        toast.error(response.data.error || 'Erro ao criar empresa');
      }
    } catch (error) {
      console.error('Erro ao criar empresa:', error);
      toast.error('Erro ao criar empresa');
    }
  };

  const handleEditCompany = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!companyForm.name.trim()) newErrors.name = 'Nome da empresa é obrigatório';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const response = await api.put(`/organizational/companies/${editingItem.id}`, companyForm);

      // Verificar se a resposta tem success ou se tem message (sucesso)
      if (response.data.success || response.data.message) {
        toast.success('Empresa atualizada com sucesso!');
        setShowCompanyModal(false);
        setEditingItem(null);
        resetCompanyForm();
        loadData();
      } else {
        toast.error(response.data.error || 'Erro ao atualizar empresa');
      }
    } catch (error) {
      console.error('Erro ao atualizar empresa:', error);
      toast.error('Erro ao atualizar empresa');
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta empresa? Todos os departamentos e cargos associados serão removidos.')) {
      return;
    }

    try {
      const response = await api.delete(`/organizational/companies/${companyId}`);

      // Verificar se a resposta tem success ou se tem message (sucesso)
      if (response.data.success || response.data.message) {
        toast.success('Empresa excluída com sucesso!');
        loadData();
      } else {
        toast.error(response.data.error || 'Erro ao excluir empresa');
      }
    } catch (error) {
      console.error('Erro ao excluir empresa:', error);
      toast.error('Erro ao excluir empresa');
    }
  };

  // Funções para departamentos
  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!departmentForm.name.trim()) newErrors.name = 'Nome do departamento é obrigatório';
    if (!departmentForm.companyId) newErrors.companyId = 'Empresa é obrigatória';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const response = await api.post(`/organizational/companies/${departmentForm.companyId}/departments`, {
        name: departmentForm.name,
        description: departmentForm.description
      });

      // Verificar se a resposta tem success ou se tem message (sucesso)
      if (response.data.success || response.data.message) {
        toast.success('Departamento criado com sucesso!');
        setShowDepartmentModal(false);
        resetDepartmentForm();
        loadData();
      } else {
        toast.error(response.data.error || 'Erro ao criar departamento');
      }
    } catch (error) {
      console.error('Erro ao criar departamento:', error);
      toast.error('Erro ao criar departamento');
    }
  };

  const handleEditDepartment = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!departmentForm.name.trim()) newErrors.name = 'Nome do departamento é obrigatório';
    if (!departmentForm.companyId) newErrors.companyId = 'Empresa é obrigatória';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const response = await api.put(`/organizational/companies/${departmentForm.companyId}/departments/${editingItem.id}`, {
        name: departmentForm.name,
        description: departmentForm.description
      });

      // Verificar se a resposta tem success ou se tem message (sucesso)
      if (response.data.success || response.data.message) {
        toast.success('Departamento atualizado com sucesso!');
        setShowDepartmentModal(false);
        setEditingItem(null);
        resetDepartmentForm();
        loadData();
      } else {
        toast.error(response.data.error || 'Erro ao atualizar departamento');
      }
    } catch (error) {
      console.error('Erro ao atualizar departamento:', error);
      toast.error('Erro ao atualizar departamento');
    }
  };

  const handleDeleteDepartment = async (departmentId, companyId) => {
    if (!window.confirm('Tem certeza que deseja excluir este departamento? Todos os cargos associados serão removidos.')) {
      return;
    }

    try {
      const response = await api.delete(`/organizational/companies/${companyId}/departments/${departmentId}`);

      // Verificar se a resposta tem success ou se tem message (sucesso)
      if (response.data.success || response.data.message) {
        toast.success('Departamento excluído com sucesso!');
        loadData();
      } else {
        toast.error(response.data.error || 'Erro ao excluir departamento');
      }
    } catch (error) {
      console.error('Erro ao excluir departamento:', error);
      toast.error('Erro ao excluir departamento');
    }
  };

  // Funções para cargos
  const handleCreatePosition = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!positionForm.name.trim()) newErrors.name = 'Nome do cargo é obrigatório';
    if (!positionForm.companyId) newErrors.companyId = 'Empresa é obrigatória';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const response = await api.post(`/organizational/companies/${positionForm.companyId}/positions`, {
        name: positionForm.name,
        description: positionForm.description,
        department_id: positionForm.departmentId || null
      });

      // Verificar se a resposta tem success ou se tem message (sucesso)
      if (response.data.success || response.data.message) {
        toast.success('Cargo criado com sucesso!');
        setShowPositionModal(false);
        resetPositionForm();
        loadData();
      } else {
        toast.error(response.data.error || 'Erro ao criar cargo');
      }
    } catch (error) {
      console.error('Erro ao criar cargo:', error);
      toast.error('Erro ao criar cargo');
    }
  };

  const handleEditPosition = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!positionForm.name.trim()) newErrors.name = 'Nome do cargo é obrigatório';
    if (!positionForm.companyId) newErrors.companyId = 'Empresa é obrigatória';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const response = await api.put(`/organizational/companies/${positionForm.companyId}/positions/${editingItem.id}`, {
        name: positionForm.name,
        description: positionForm.description,
        department_id: positionForm.departmentId || null
      });

      // Verificar se a resposta tem success ou se tem message (sucesso)
      if (response.data.success || response.data.message) {
        toast.success('Cargo atualizado com sucesso!');
        setShowPositionModal(false);
        setEditingItem(null);
        resetPositionForm();
        loadData();
      } else {
        toast.error(response.data.error || 'Erro ao atualizar cargo');
      }
    } catch (error) {
      console.error('Erro ao atualizar cargo:', error);
      toast.error('Erro ao atualizar cargo');
    }
  };

  const handleDeletePosition = async (positionId, companyId) => {
    if (!window.confirm('Tem certeza que deseja excluir este cargo?')) {
      return;
    }

    try {
      const response = await api.delete(`/organizational/companies/${companyId}/positions/${positionId}`);

      // Verificar se a resposta tem success ou se tem message (sucesso)
      if (response.data.success || response.data.message) {
        toast.success('Cargo excluído com sucesso!');
        loadData();
      } else {
        toast.error(response.data.error || 'Erro ao excluir cargo');
      }
    } catch (error) {
      console.error('Erro ao excluir cargo:', error);
      toast.error('Erro ao excluir cargo');
    }
  };

  // Funções de reset dos formulários
  const resetCompanyForm = () => {
    setCompanyForm({
      name: '',
      cnpj: '',
      address: '',
      phone: '',
      email: ''
    });
    setErrors({});
  };

  const resetDepartmentForm = () => {
    setDepartmentForm({
      name: '',
      description: '',
      companyId: ''
    });
    setErrors({});
  };

  const resetPositionForm = () => {
    setPositionForm({
      name: '',
      description: '',
      companyId: '',
      departmentId: ''
    });
    setErrors({});
  };

  // Funções para abrir modais
  const openCompanyModal = (company = null) => {
    if (company) {
      setEditingItem(company);
      setCompanyForm({
        name: company.name,
        cnpj: company.cnpj || '',
        address: company.address || '',
        phone: company.phone || '',
        email: company.email || ''
      });
    } else {
      setEditingItem(null);
      resetCompanyForm();
    }
    setShowCompanyModal(true);
  };

  const openDepartmentModal = (department = null) => {
    if (department) {
      setEditingItem(department);
      setDepartmentForm({
        name: department.name,
        description: department.description || '',
        companyId: department.company_id
      });
    } else {
      setEditingItem(null);
      resetDepartmentForm();
    }
    setShowDepartmentModal(true);
  };

  const openPositionModal = (position = null) => {
    if (position) {
      setEditingItem(position);
      setPositionForm({
        name: position.name,
        description: position.description || '',
        companyId: position.company_id,
        departmentId: position.department_id
      });
    } else {
      setEditingItem(null);
      resetPositionForm();
    }
    setShowPositionModal(true);
  };

  // Funções auxiliares
  const toggleCompanyExpansion = (companyId) => {
    const newExpanded = new Set(expandedCompanies);
    if (newExpanded.has(companyId)) {
      newExpanded.delete(companyId);
    } else {
      newExpanded.add(companyId);
    }
    setExpandedCompanies(newExpanded);
  };

  const getDepartmentsByCompany = (companyId) => {
    return departments.filter(dept => dept.company_id === companyId);
  };

  const getPositionsByCompany = (companyId) => {
    return positions.filter(pos => pos.company_id === companyId);
  };

  const getPositionsByDepartment = (companyId, departmentId) => {
    return positions.filter(pos => 
      pos.company_id === companyId && pos.department_id === departmentId
    );
  };

  // Verificar se o usuário é administrador
  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Acesso Negado</h1>
          <p className="text-gray-600">Apenas administradores podem acessar esta página.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <Layout>
      <div>
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Configurações Organizacionais</h1>
          <p className="text-gray-600">Gerencie empresas, departamentos e cargos do sistema</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('companies')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'companies'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Building2 className="h-5 w-5" />
              <span>Empresas</span>
            </button>
            <button
              onClick={() => setActiveTab('departments')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'departments'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Users className="h-5 w-5" />
              <span>Departamentos</span>
            </button>
            <button
              onClick={() => setActiveTab('positions')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'positions'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Briefcase className="h-5 w-5" />
              <span>Cargos</span>
            </button>
          </div>
        </div>

        {/* Conteúdo das Tabs */}
        {activeTab === 'companies' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Empresas</h2>
                <button
                  onClick={() => openCompanyModal()}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Nova Empresa
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CNPJ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contato
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {companies.map((company) => (
                    <tr key={company.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{company.name}</div>
                        <div className="text-sm text-gray-500">{company.address}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {company.cnpj || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>{company.phone || 'N/A'}</div>
                        <div>{company.email || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openCompanyModal(company)}
                            className="text-primary-600 hover:text-primary-900 transition-colors"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCompany(company.id)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'departments' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Departamentos</h2>
                <button
                  onClick={() => openDepartmentModal()}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Novo Departamento
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Empresa
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {departments.map((department) => (
                    <tr key={department.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{department.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {department.companyName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {department.description || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openDepartmentModal(department)}
                            className="text-primary-600 hover:text-primary-900 transition-colors"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteDepartment(department.id, department.company_id)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'positions' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Cargos</h2>
                <button
                  onClick={() => openPositionModal()}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Novo Cargo
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Empresa
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Departamento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {positions.map((position) => (
                    <tr key={position.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{position.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {position.companyName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {position.department_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {position.description || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openPositionModal(position)}
                            className="text-primary-600 hover:text-primary-900 transition-colors"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeletePosition(position.id, position.company_id)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal de Empresa */}
        {showCompanyModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingItem ? 'Editar Empresa' : 'Nova Empresa'}
                </h3>
                <button
                  onClick={() => setShowCompanyModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <form onSubmit={editingItem ? handleEditCompany : handleCreateCompany} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome da Empresa *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={companyForm.name}
                    onChange={(e) => setCompanyForm({...companyForm, name: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CNPJ
                  </label>
                  <input
                    type="text"
                    name="cnpj"
                    value={companyForm.cnpj}
                    onChange={(e) => setCompanyForm({...companyForm, cnpj: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="00.000.000/0000-00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Endereço
                  </label>
                  <textarea
                    name="address"
                    value={companyForm.address}
                    onChange={(e) => setCompanyForm({...companyForm, address: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={companyForm.phone}
                    onChange={(e) => setCompanyForm({...companyForm, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={companyForm.email}
                    onChange={(e) => setCompanyForm({...companyForm, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCompanyModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    {editingItem ? 'Salvar Alterações' : 'Criar Empresa'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de Departamento */}
        {showDepartmentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingItem ? 'Editar Departamento' : 'Novo Departamento'}
                </h3>
                <button
                  onClick={() => setShowDepartmentModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <form onSubmit={editingItem ? handleEditDepartment : handleCreateDepartment} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Empresa *
                  </label>
                  <select
                    name="companyId"
                    value={departmentForm.companyId}
                    onChange={(e) => setDepartmentForm({...departmentForm, companyId: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.companyId ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Selecione uma empresa</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                  {errors.companyId && <p className="text-red-500 text-sm mt-1">{errors.companyId}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Departamento *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={departmentForm.name}
                    onChange={(e) => setDepartmentForm({...departmentForm, name: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <textarea
                    name="description"
                    value={departmentForm.description}
                    onChange={(e) => setDepartmentForm({...departmentForm, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowDepartmentModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    {editingItem ? 'Salvar Alterações' : 'Criar Departamento'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de Cargo */}
        {showPositionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingItem ? 'Editar Cargo' : 'Novo Cargo'}
                </h3>
                <button
                  onClick={() => setShowPositionModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <form onSubmit={editingItem ? handleEditPosition : handleCreatePosition} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Empresa *
                  </label>
                  <select
                    name="companyId"
                    value={positionForm.companyId}
                    onChange={(e) => {
                      setPositionForm({
                        ...positionForm, 
                        companyId: e.target.value,
                        departmentId: '' // Reset department when company changes
                      });
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.companyId ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Selecione uma empresa</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                  {errors.companyId && <p className="text-red-500 text-sm mt-1">{errors.companyId}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Departamento
                  </label>
                  <select
                    name="departmentId"
                    value={positionForm.departmentId}
                    onChange={(e) => setPositionForm({...positionForm, departmentId: e.target.value})}
                    disabled={!positionForm.companyId}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                  >
                    <option value="">Selecione um departamento (opcional)</option>
                    {departments
                      .filter(dept => dept.company_id === parseInt(positionForm.companyId))
                      .map(department => (
                        <option key={department.id} value={department.id}>{department.name}</option>
                      ))
                    }
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Cargo *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={positionForm.name}
                    onChange={(e) => setPositionForm({...positionForm, name: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <textarea
                    name="description"
                    value={positionForm.description}
                    onChange={(e) => setPositionForm({...positionForm, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowPositionModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    {editingItem ? 'Salvar Alterações' : 'Criar Cargo'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OrganizationalSettings;
